import { getRequest, postRequest } from 'features/common'
import {
  CARTO_IGNORED_PROCESSING_JOBS,
  DryRunPayload,
  ImportingJobPayload,
  ProcessingJob,
  ProcessingJobPayload,
  ProcessingJobResponse,
  ProcessingJobStatus
} from '../types'
import { checkPermissions } from './dryRuns'
import { getLocalStorageItem, setLocalStorageItem } from 'app/api/localStorage'
import { encodeUriParams } from 'features/common/utils/searchParamsUtils'
import { getClient } from 'features/common/utils/clientParameter'
import { batchApiUrl, deleteBatchJob } from './batchApi'

export const getProcessingJobsList = async (params: ImportingJobPayload): Promise<ProcessingJobResponse[]> => {
  return await getRequest(
    `${batchApiUrl}/jobs?${encodeUriParams({
      user_id: params.owner,
      created_since: params.createdSince,
      status: params.status,
      client: getClient()
    })}`
  )
}

export const createProcessingJob = async ({
  connection,
  processingJobPayload
}: {
  connection: string
  processingJobPayload: ProcessingJobPayload
}): Promise<ProcessingJob> => {
  const url = `${batchApiUrl}/${connection}/job?client=${getClient()}`
  const { query, queryParameters, metadata } = processingJobPayload

  const ProcessingJobResponse: ProcessingJobResponse = await postRequest(url, {
    query: typeof query === 'string' ? query : query[0],
    queryParameters,
    metadata: { ...metadata, location: null }
  })
  const processingJob: ProcessingJob = {
    connection,
    jobId: ProcessingJobResponse.externalId,
    response: ProcessingJobResponse
  }
  return processingJob
}

export const createDryRun = async (payload: DryRunPayload) => {
  return checkPermissions(payload)
}

export const checkProcessingJob = async ({
  connection,
  jobId
}: {
  connection: string
  jobId: string
}): Promise<ProcessingJobStatus> => {
  const url = `${batchApiUrl}/${connection}/job/${jobId}?client=${getClient()}`
  const jobStatus: ProcessingJobStatus = await getRequest(url)
  return jobStatus
}

export const deleteProcessingJob = async ({
  connection: connectionName,
  jobId
}: {
  connection: string
  jobId: string
}): Promise<{ status: string }> => {
  return deleteBatchJob({ connectionName, jobId })
}

export const setIgnoredProcessingJobs = ({ jobs }: { jobs: ProcessingJob[] }): Promise<ProcessingJob[]> => {
  const currentlyIgnored = getLocalStorageItem(CARTO_IGNORED_PROCESSING_JOBS)
  const finallyIgnored = currentlyIgnored ? [...currentlyIgnored, ...jobs] : jobs
  setLocalStorageItem(CARTO_IGNORED_PROCESSING_JOBS, finallyIgnored)
  return new Promise((resolve, reject) => {
    const processingJobsString = getLocalStorageItem(CARTO_IGNORED_PROCESSING_JOBS)
    if (processingJobsString) {
      resolve(processingJobsString)
    } else {
      reject(jobs)
    }
  })
}

export const addToIgnoredProcessingJobs = (job: ProcessingJob): Promise<ProcessingJob[]> => {
  return new Promise((resolve) => {
    const processingJobsString = getLocalStorageItem(CARTO_IGNORED_PROCESSING_JOBS)
    const ignoredProcessingJobs = processingJobsString ? [...processingJobsString, job] : [job]
    setLocalStorageItem(CARTO_IGNORED_PROCESSING_JOBS, ignoredProcessingJobs)
    resolve(ignoredProcessingJobs)
  })
}
